import { api } from "../api/client";

/**
 * Web push (VAPID): after the browser notification permission is granted,
 * subscribe the service worker and register the endpoint with the API.
 * Silent on failure — push is a progressive enhancement.
 */

const SUBSCRIBED_KEY = "nelurio.push.subscribed";

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(normalized);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output as unknown as Uint8Array<ArrayBuffer>;
}

/** Subscribe the SW to push and register the endpoint. Silent on any failure. */
export async function enableWebPush(): Promise<void> {
  try {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    if (existing && !existing.expirationTime) {
      await syncSubscription(existing);
      return;
    }
    const { publicKey } = await api<{ publicKey: string }>("/push/vapid-public");
    if (!publicKey) return;
    const applicationServerKey = urlBase64ToUint8Array(publicKey);
    const subscribeOptions = { userVisibleOnly: true, applicationServerKey: applicationServerKey as unknown as BufferSource };
    const subscription = await registration.pushManager.subscribe(subscribeOptions);
    await syncSubscription(subscription);
    try { window.localStorage.setItem(SUBSCRIBED_KEY, new Date().toISOString()); } catch { /* private mode */ }
  } catch { /* push unavailable — not critical */ }
}

/** Push the stored endpoint to the API (idempotent upsert). */
async function syncSubscription(subscription: PushSubscription) {
  const raw = subscription.toJSON();
  const keys = raw.keys as { p256dh?: string; auth?: string } | undefined;
  if (!keys?.p256dh || !keys.auth) return;
  await api("/push/subscribe", {
    method: "POST",
    body: JSON.stringify({ endpoint: subscription.endpoint, p256dh: keys.p256dh, auth: keys.auth, userAgent: navigator.userAgent.slice(0, 180) }),
  });
}

/** Is a live push subscription present in this browser? */
export async function pushSubscribed(): Promise<boolean> {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
    const registration = await navigator.serviceWorker.ready;
    return Boolean(await registration.pushManager.getSubscription());
  } catch {
    return false;
  }
}

/** Connection state for the settings toggle. */
export type PushConnection = "on" | "off" | "blocked" | "unsupported";

export async function pushConnection(): Promise<PushConnection> {
  if (typeof Notification === "undefined" || !("serviceWorker" in navigator) || !("PushManager" in window)) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  return (await pushSubscribed()) ? "on" : "off";
}

/** Turn the connection OFF: unsubscribe this browser and forget the flag. */
export async function disableWebPush(): Promise<void> {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) await subscription.unsubscribe(); // the push service drops the endpoint; the API prunes it on next send
    window.localStorage.removeItem(SUBSCRIBED_KEY);
  } catch { /* best effort */ }
}
